import axios from 'axios';

/**
 * URL base del backend API (ej: 'http://192.168.1.12:3001/api' o '/api')
 */
export const apiBaseUrl = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');

/**
 * Determina si hay un backend Express SSE disponible.
 * En Netlify estático (sin servidor Node.js independiente), se deshabilita para evitar 
 * errores 404 en /api/sync/command y fallos de MIME type en EventSource,
 * utilizando la sincronización nativa en la nube con Firebase Firestore.
 */
export const isSyncBackendAvailable =
  import.meta.env.VITE_SYNC_BACKEND_ENABLED !== 'false' &&
  Boolean(apiBaseUrl) &&
  !apiBaseUrl.includes('netlify.app');

/**
 * API Client - Comunica el frontend con el backend Express
 * Todas las llamadas a datos pasan por aquí (nunca directo a Firestore).
 */
const apiClient = axios.create({
  baseURL: apiBaseUrl,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor: inyecta el Bearer token de Firebase Auth en cada request
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('garaoke-auth-token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor: maneja errores globales (401 → redirect a login)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('garaoke-auth-token');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

/**
 * API Methods
 */
export const api = {
  // ── Auth ──
  verifyAuth: () => apiClient.get('/auth/verify'),

  // ── User Settings ──
  getUserSettings: () => apiClient.get('/users/settings'),
  saveUserSettings: (settings) => apiClient.put('/users/settings', settings),
  updateUserField: (field, value) => apiClient.patch('/users/field', { field, value }),

  // ── YouTube Search (proxied through backend) ──
  searchYouTube: (query, karaokeOnly = true, maxResults = 15) =>
    apiClient.get('/youtube/search', {
      params: { q: query, karaokeOnly, maxResults },
    }),

  // ── Playlists ──
  getPlaylists: () => apiClient.get('/playlists'),
  savePlaylist: (playlist) => apiClient.post('/playlists', playlist),
  updatePlaylist: (id, data) => apiClient.put(`/playlists/${id}`, data),
  deletePlaylist: (id) => apiClient.delete(`/playlists/${id}`),

  // ── Payments ──
  getPaymentProcessors: () => apiClient.get('/payments/processors'),
  createCheckout: (data) => apiClient.post('/payments/checkout', data),
  activateFreePlan: () => apiClient.post('/payments/activate-free'),
  getPaymentHistory: () => apiClient.get('/payments'),
  cancelSubscription: () => apiClient.post('/payments/cancel'),

  // ── Subscription ──
  getSubscriptionStatus: () => apiClient.get('/subscriptions/status'),

  // ── Support ──
  sendSupportMessage: (data) => apiClient.post('/support', data),
};

export default api;
